import React, { useState, useEffect } from "react";
import {
  Search,
  BarChart3,
  ChevronRight,
  TrendingUp,
  Loader2,
} from "lucide-react";
import api from "../api/axiosInstance";
import ProjectProfitability from "./ProjectProfitability";

const ProjectAuditManager = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  useEffect(() => {
    if (!selectedProjectId) fetchProjects();
  }, [selectedProjectId]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await api.get("get_projects.php");
      setProjects(res.data.projects || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- VIEW SWITCHER ---
  if (selectedProjectId) {
    return (
      <ProjectProfitability
        projectId={selectedProjectId}
        onBack={() => setSelectedProjectId(null)}
      />
    );
  }

  return (
    <div className="space-y-8 p-4 animate-in fade-in duration-500">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase">
            Site Profitability
          </h1>
          <p className="text-slate-500 font-medium italic">
            Audit specific project margins below.
          </p>
        </div>
        <div className="flex items-center gap-4 bg-white px-6 py-3 rounded-2xl border border-slate-100 shadow-sm">
          <Search size={18} className="text-slate-400" />
          <input
            className="bg-transparent outline-none font-bold text-xs uppercase"
            placeholder="Search sites..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="p-20 text-center">
          <Loader2 className="animate-spin inline text-indigo-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects
            .filter((p) =>
              p.project_name.toLowerCase().includes(searchTerm.toLowerCase()),
            )
            .map((project) => (
              <div
                key={project.id}
                // CRITICAL: The click event is here on the parent
                onClick={() => setSelectedProjectId(project.id)}
                className="group bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm hover:shadow-2xl hover:border-indigo-100 transition-all cursor-pointer relative overflow-hidden active:scale-95"
              >
                {/* pointer-events-none ensures clicks go through to the parent div */}
                <div className="relative z-10 pointer-events-none">
                  <div className="flex justify-between items-start mb-6">
                    <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                      <BarChart3 size={24} />
                    </div>
                    <span className="text-[9px] font-black uppercase px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full">
                      {project.status}
                    </span>
                  </div>

                  <h3 className="text-xl font-black text-slate-900 uppercase italic leading-tight group-hover:text-indigo-600 transition-colors">
                    {project.project_name}
                  </h3>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-slate-50">
                    <div>
                      <p className="text-[8px] font-black text-slate-400 uppercase">
                        Est. Budget
                      </p>
                      <p className="font-black text-slate-900 italic">
                        ₹{parseFloat(project.total_budget).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-all">
                      <ChevronRight size={20} />
                    </div>
                  </div>
                </div>
                <TrendingUp
                  size={120}
                  className="absolute -right-8 -bottom-8 text-slate-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                />
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default ProjectAuditManager;
